import { Poster } from '../types';
import Card from './ui/Card';

interface PreviewProps {
  poster: Poster;
}

export default function Preview({ poster }: PreviewProps) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      {/* Main Poster Card */}
      <Card className="mb-8">
        {/* Title Section */}
        <div className="text-center mb-8 pb-8 border-b-2 border-primary-200">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold 
                         text-neutral-900 mb-6 leading-tight tracking-tight">
        {poster.title}
      </h1>

          {/* Authors & Affiliations */}
          <div className="space-y-3">
            <p className="text-lg md:text-xl text-neutral-800 font-medium 
                          leading-relaxed">
          {poster.authors.join(', ')}
        </p>
            <p className="text-sm md:text-base text-neutral-600 
                          leading-relaxed max-w-4xl mx-auto">
          {poster.affiliations.join(', ')}
        </p>
      </div>
        </div>

        {/* Summary Section */}
      {poster.summary && (
          <div className="mb-8 pb-8 border-b border-neutral-200">
            <h2 className="section-header text-xl md:text-2xl">
              Summary
            </h2>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed 
                          whitespace-pre-wrap">
              {poster.summary}
            </p>
        </div>
      )}

        {/* Key Takeaways Section */}
      {poster.keyTakeaways.length > 0 && (
          <div className="mb-8 pb-8 border-b border-neutral-200">
            <h2 className="section-header text-xl md:text-2xl">
              Key Takeaways
            </h2>
            <ul className="space-y-3 pl-6">
            {poster.keyTakeaways.map((takeaway, index) => (
                <li 
                  key={index} 
                  className="text-base md:text-lg text-neutral-700 leading-relaxed
                             relative before:content-['•'] before:absolute before:-left-6
                             before:text-primary-600 before:text-2xl before:font-bold"
                >
                {takeaway}
              </li>
            ))}
          </ul>
        </div>
      )}

        {/* Content Sections */}
        <div className="space-y-8">
      {poster.sections.map((section, index) => (
            <div 
              key={index} 
              className="pb-8 border-b border-neutral-200 last:border-b-0 last:pb-0"
            >
              <h2 className="section-header text-xl md:text-2xl">
                {section.title}
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-base md:text-lg text-neutral-700 leading-relaxed 
                              whitespace-pre-wrap">
                  {section.content}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Figures Section */}
      {poster.figures.length > 0 && (
          <div className="mt-12 pt-8 border-t-2 border-primary-200">
            <h2 className="section-header text-xl md:text-2xl mb-6">
              Figures
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {poster.figures.map((figure, index) => (
                <Card key={index} hover className="overflow-hidden">
                {figure.image && (
                    <div className="mb-4 overflow-hidden rounded-lg bg-neutral-100">
                  <img
                    src={figure.image}
                    alt={figure.description}
                        className="w-full h-auto object-cover transition-transform 
                                   duration-300 hover:scale-105"
                      />
                    </div>
                )}
                  <p className="text-sm md:text-base text-neutral-600 text-center 
                                font-medium">
                  {figure.description}
                </p>
                </Card>
            ))}
          </div>
        </div>
      )}
      </Card>
    </div>
  );
}
